/**
 * @file Who may see the `/host` area, and where everyone else goes.
 *
 * Two layers ask this question and must never answer it differently:
 *
 *  1. **MainLayout's PortalProtector**, which now wraps the host pages and
 *     bounces anyone signed out.
 *  2. **Each host entry's own guard** — services, packages, builder — which
 *     runs before their HostOnboardingProvider mounts, because left to the
 *     provider an anonymous visitor's status fetch would 400 first and send a
 *     first-timer to `/host/login?reason=session`: a wrong and slightly
 *     alarming answer to "I clicked an ad".
 *
 * Both effects fire in the same commit, so if they disagreed the visitor would
 * be bounced twice and land wherever the parent happened to win. They share
 * `hostSignedOutRoute` instead.
 *
 * The signed-out redirect goes to **signup** for the services and packages
 * area, deliberately: `/host?type=medical_provider` is a marketable recruiting
 * URL, so the `?type=` param is carried through rather than dropped (plan §3.2,
 * R4). Two areas are exceptions, because whoever opens them already has an
 * account:
 *
 *  - the onboarding wizard, where someone resumes an application, goes to
 *    login;
 *  - the inbox, whose links arrive in a host's enquiry emails, goes to login
 *    with the path as `?next=`, so signing in opens that enquiry rather than
 *    the wizard (host-inbox-tech-design §4.5.10, §9.2);
 *  - the calendar, likewise: only an approved host has one, and a booking's
 *    warning links to a month on it (host-availability FE-2.1).
 *
 * This is a route decision, never authorization: the server re-reads
 * `user_type` from the database on every host endpoint, so a tampered Redux
 * store buys nothing but a page with no data on it.
 *
 * Plain .js: no JSX.
 */
import { HOST_TYPES } from '../constants/host-taxonomy';
import { withNext } from './return-to';

export const HOST_USER_TYPE = 'travel_host';

const SIGNUP_ROUTE = '/host/signup';
const LOGIN_ROUTE = '/host/login';
const MEMBER_ROUTE = '/my-apps';
const ONBOARDING_ROUTE = '/host/onboarding';
const INBOX_ROUTE = '/host/inbox';
const CALENDAR_ROUTE = '/host/calendar';

const VALID_TYPES = [HOST_TYPES.MEDICAL_PROVIDER, HOST_TYPES.TOUR_GUIDE];

const isUnder = (pathname, route) => pathname === route || pathname.startsWith(`${route}/`);

/**
 * The host's work pages, which send a signed-out visitor to login with
 * `next`: `/host/inbox`, `/host/calendar` and anything under them, never
 * `/host/inboxes`. The same rule `readSafeNext` applies.
 */
const isLoginWithNextPath = (pathname) => isUnder(pathname, INBOX_ROUTE) || isUnder(pathname, CALENDAR_ROUTE);

/** `/host/signup?type=tour_guide`, or bare signup when there is no valid type. */
export const hostSignupRoute = (hostType) => (hostType ? `${SIGNUP_ROUTE}?type=${hostType}` : SIGNUP_ROUTE);

/** True for `/host` and anything under it — never for `/hostel`-style near misses. */
export const isHostAreaPath = (pathname = '') => pathname === '/host' || pathname.startsWith('/host/');

/**
 * Where a signed-out visitor to a `/host*` URL belongs.
 *
 * @param {string} pathname The location being blocked.
 * @param {string} search Its query string, so `?type=` survives the bounce,
 *   and an inbox link's `?src=email` (or the calendar's `?month=`) rides
 *   along inside `next`.
 */
export const hostSignedOutRoute = (pathname = '', search = '') => {
  if (pathname.startsWith(ONBOARDING_ROUTE)) return LOGIN_ROUTE;
  if (isLoginWithNextPath(pathname)) {
    const query = search ? `?${search.replace(/^\?/, '')}` : '';
    return withNext(LOGIN_ROUTE, `${pathname}${query}`);
  }

  const requested = new URLSearchParams(search).get('type');
  return hostSignupRoute(VALID_TYPES.includes(requested) ? requested : null);
};

/**
 * Where to send a visitor who cannot use this page, or null if they can.
 *
 * A member or an admin gets no error screen: they have not done anything
 * wrong, they are just in the wrong place.
 *
 * @param {string|undefined} userType `state.user.data.userType` from Redux.
 * @param {string|null} deepLinkType A validated `?type=` value, or null.
 */
export const hostAreaRedirect = (userType, deepLinkType = null) => {
  if (!userType) return hostSignupRoute(deepLinkType);
  if (userType !== HOST_USER_TYPE) return MEMBER_ROUTE;
  return null;
};

export default hostAreaRedirect;
